
import React, { useEffect, useState } from "react";
import { RouterProvider } from "react-router-dom";
import { QueryClientProvider } from "@tanstack/react-query";
// Instance déplacée dans `@/lib/queryClient` pour être joignable hors React
// (vidage du cache au changement de salle, cf. userStore.switchGym).
import { queryClient } from "@/lib/queryClient";
import { router } from "./routes";
import { ThemeProvider } from "./components/theme/ThemeProvider";
import { Toaster } from "./components/ui/toaster";
import { Toaster as Sonner } from "./components/ui/sonner";
import { CurrencyProvider } from "./contexts/CurrencyContext";
import { AuthProvider } from "./contexts/AuthContext";
import "./App.css";
import { SettingsInitializer } from './hooks/useSettingsInitialization';
import { useForceUpdateCheck } from "./hooks/useForceUpdateCheck";
import { useCapacitorLifecycle } from "./hooks/useCapacitorLifecycle";
import { useVersionPolling } from "./hooks/useVersionPolling";
import { clearAppCaches } from "./utils/clearAppCaches";
import { isChunkLoadError } from "./utils/isChunkLoadError";
import { UpdateRecoveryScreen } from "./components/common/UpdateRecoveryScreen";
import ForceUpdatePage from "./pages/ForceUpdatePage";

const CHUNK_ATTEMPTS_KEY = 'chunk_reload_attempts';
const MAX_CHUNK_RELOAD_ATTEMPTS = 2;

function App() {
  const { forceUpdate, isChecking } = useForceUpdateCheck();
  const [recoveryMode, setRecoveryMode] = useState(false);

  useVersionPolling();

  useCapacitorLifecycle();

  // Handle Vite chunk loading errors (module script failed)
  useEffect(() => {
    const handleChunkError = async (source: string, detail?: unknown) => {
      const attempts = parseInt(sessionStorage.getItem(CHUNK_ATTEMPTS_KEY) || '0', 10);
      console.warn(`[App] Chunk error (${source}) — attempt ${attempts + 1}/${MAX_CHUNK_RELOAD_ATTEMPTS + 1}`, detail);

      if (attempts >= MAX_CHUNK_RELOAD_ATTEMPTS) {
        console.error('[App] Max chunk reload attempts reached, showing recovery screen');
        setRecoveryMode(true);
        return;
      }

      sessionStorage.setItem(CHUNK_ATTEMPTS_KEY, String(attempts + 1));
      await clearAppCaches();
      const baseUrl = window.location.pathname + window.location.hash;
      const sep = baseUrl.includes('?') ? '&' : '?';
      window.location.replace(`${baseUrl}${sep}_cb=${Date.now()}`);
    };

    const handlePreloadError = (event: Event) => {
      handleChunkError('vite:preloadError', event);
    };

    const handleGlobalError = (event: ErrorEvent) => {
      // Matcher volontairement strict (cf. isChunkLoadError) : une simple
      // erreur réseau au retour de background ne doit JAMAIS recharger
      // l'app. Les vrais échecs de chunk passent aussi par vite:preloadError.
      if (isChunkLoadError(event.message)) handleChunkError('window.error', event.message);
    };

    window.addEventListener('vite:preloadError', handlePreloadError);
    window.addEventListener('error', handleGlobalError);

    // Reset attempts counter after 60s of stable runtime
    const timer = setTimeout(() => {
      sessionStorage.removeItem(CHUNK_ATTEMPTS_KEY);
    }, 60000);

    return () => {
      window.removeEventListener('vite:preloadError', handlePreloadError);
      window.removeEventListener('error', handleGlobalError);
      clearTimeout(timer);
    };
  }, []);

  useEffect(() => {
    // Enhanced redirect handling from 404.html
    const redirectUrl = sessionStorage.getItem('redirect');
    if (redirectUrl) {
      console.log('[App] Found redirect URL:', redirectUrl);
      
      // Check if it's a password reset URL
      if (redirectUrl.includes('/set-password') || 
          redirectUrl.includes('type=recovery') || 
          redirectUrl.includes('access_token=')) {
        console.log('[App] Processing password reset URL');
        
        try {
          const url = new URL(redirectUrl);
          let hashPart = url.hash;
          
          // If parameters are in search, move them to hash
          if (!hashPart && url.search) {
            hashPart = '#' + url.search.substring(1);
          }
          
          // Extract parameters from hash
          if (hashPart) {
            console.log('[App] Extracted hash part:', hashPart.substring(0, 30) + '...');
            
            // Ensure the hash is preserved in the current URL
            if (!window.location.hash && hashPart) {
              console.log('[App] Setting hash in current URL');
              // Using history API to avoid page reload
              window.history.replaceState(
                null, 
                document.title, 
                window.location.pathname + hashPart
              );
            }
          }
        } catch (error) {
          console.error('[App] Error processing redirect URL:', error);
        }
      }
      
      // Clean up
      sessionStorage.removeItem('redirect');
    }
  }, []);

  if (recoveryMode) {
    return <UpdateRecoveryScreen />;
  }

  if (isChecking) {
    return <div>Chargement...</div>; // Splash/Loader, optionnel
  }

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider defaultTheme="light" storageKey="vite-ui-theme">
      {forceUpdate ? (
        <ForceUpdatePage />
      ) : (
        <AuthProvider>
          <CurrencyProvider>
            <SettingsInitializer />
            <RouterProvider router={router} />
            <Toaster />
            <Sonner position="top-right" />
          </CurrencyProvider>
        </AuthProvider>)}
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
